import type { Database } from './supabase';

type Job = Database['public']['Tables']['jobs']['Row'];
type Profile = Database['public']['Tables']['profiles']['Row'];

export interface MatchBreakdown {
  overall: number;
  skills: number;
  experience: number;
  salary: number;
  location: number;
  jobType: number;
  details: {
    matchedSkills: string[];
    missingSkills: string[];
    experienceMatch: 'perfect' | 'close' | 'mismatch';
    salaryMatch: 'within_range' | 'above_range' | 'below_range' | 'unknown';
    locationMatch: 'perfect' | 'remote_ok' | 'mismatch';
    jobTypeMatch: boolean;
  };
}

const EXPERIENCE_LEVELS = ['entry', 'mid', 'senior', 'executive'];

/**
 * Calculate match score between a job seeker profile and a job posting
 *
 * @description Computes a weighted compatibility score (0-100) between a job seeker's profile
 * and a job posting. The algorithm considers multiple factors with different weights:
 * - Skills: 40% (most important)
 * - Experience Level: 20%
 * - Salary Expectations: 15%
 * - Location/Remote: 15%
 * - Job Type: 10%
 *
 * @param {Profile} profile - The job seeker's profile from the database
 * @param {Job} job - The job posting to match against
 *
 * @returns {MatchBreakdown} An object containing:
 * - overall: Weighted total score (0-100)
 * - skills/experience/salary/location/jobType: Individual category scores
 * - details: Breakdown of matched/missing skills, experience level match, etc.
 *
 * @example
 * ```typescript
 * const matchScore = calculateMatchScore(userProfile, jobPosting);
 * console.log(matchScore.overall); // 85
 * console.log(matchScore.details.matchedSkills); // ['React', 'TypeScript']
 * ```
 */
export function calculateMatchScore(profile: Profile, job: Job): MatchBreakdown {
  // Initialize scores
  let skillsScore = 0;
  let experienceScore = 0;
  let salaryScore = 0;
  let locationScore = 0;
  let jobTypeScore = 0;

  // Skills matching (40% weight - most important)
  const matchedSkills: string[] = [];
  const missingSkills: string[] = [];

  if (job.skills && job.skills.length > 0) {
    const userSkills = (profile.skills || []).map(s => s.toLowerCase());

    job.skills.forEach(jobSkill => {
      if (userSkills.includes(jobSkill.toLowerCase())) {
        matchedSkills.push(jobSkill);
      } else {
        missingSkills.push(jobSkill);
      }
    });

    skillsScore = job.skills.length > 0
      ? (matchedSkills.length / job.skills.length) * 100
      : 50; // Neutral if no skills required
  } else {
    skillsScore = 50; // Neutral if job has no skills listed
  }

  // Experience level matching (20% weight)
  let experienceMatch: 'perfect' | 'close' | 'mismatch' = 'mismatch';

  if (profile.experience_level && job.experience_level) {
    const userLevelIndex = EXPERIENCE_LEVELS.indexOf(profile.experience_level);
    const jobLevelIndex = EXPERIENCE_LEVELS.indexOf(job.experience_level);

    if (userLevelIndex === jobLevelIndex) {
      experienceScore = 100;
      experienceMatch = 'perfect';
    } else if (Math.abs(userLevelIndex - jobLevelIndex) === 1) {
      experienceScore = 70;
      experienceMatch = 'close';
    } else {
      experienceScore = 30;
      experienceMatch = 'mismatch';
    }
  } else {
    experienceScore = 50; // Neutral if not specified
    experienceMatch = 'mismatch';
  }

  // Salary matching (15% weight)
  let salaryMatch: 'within_range' | 'above_range' | 'below_range' | 'unknown' = 'unknown';

  if (profile.min_salary && profile.max_salary && job.salary_min && job.salary_max) {
    // Check if ranges overlap
    const userMin = profile.min_salary;
    const userMax = profile.max_salary;
    const jobMin = job.salary_min;
    const jobMax = job.salary_max;

    // Perfect overlap
    if (userMin <= jobMax && userMax >= jobMin) {
      const overlapStart = Math.max(userMin, jobMin);
      const overlapEnd = Math.min(userMax, jobMax);
      const overlapSize = overlapEnd - overlapStart;
      const userRange = userMax - userMin;

      salaryScore = Math.min(100, (overlapSize / userRange) * 100);
      salaryMatch = 'within_range';
    } else if (jobMax < userMin) {
      salaryScore = 20; // Job pays below user's minimum
      salaryMatch = 'below_range';
    } else {
      salaryScore = 40; // Job pays above user's maximum (could still be okay)
      salaryMatch = 'above_range';
    }
  } else {
    salaryScore = 50; // Neutral if salary not specified
    salaryMatch = 'unknown';
  }

  // Location/Remote matching (15% weight)
  let locationMatch: 'perfect' | 'remote_ok' | 'mismatch' = 'mismatch';

  if (job.remote && profile.remote_preference) {
    locationScore = 100;
    locationMatch = 'perfect';
  } else if (job.remote || profile.remote_preference) {
    locationScore = 80;
    locationMatch = 'remote_ok';
  } else if (profile.location && job.location) {
    // Simple location match (could be enhanced with geocoding)
    const userLocationLower = profile.location.toLowerCase();
    const jobLocationLower = job.location.toLowerCase();

    if (userLocationLower.includes(jobLocationLower) || jobLocationLower.includes(userLocationLower)) {
      locationScore = 100;
      locationMatch = 'perfect';
    } else {
      locationScore = 30;
      locationMatch = 'mismatch';
    }
  } else {
    locationScore = 50; // Neutral if not specified
    locationMatch = 'mismatch';
  }

  // Job type matching (10% weight)
  const jobTypeMatch = profile.desired_job_types?.includes(job.job_type) ?? false;
  jobTypeScore = jobTypeMatch ? 100 : (profile.desired_job_types?.length ? 20 : 50);

  // Calculate weighted overall score
  const overallScore = Math.round(
    skillsScore * 0.40 +
    experienceScore * 0.20 +
    salaryScore * 0.15 +
    locationScore * 0.15 +
    jobTypeScore * 0.10
  );

  return {
    overall: overallScore,
    skills: Math.round(skillsScore),
    experience: Math.round(experienceScore),
    salary: Math.round(salaryScore),
    location: Math.round(locationScore),
    jobType: Math.round(jobTypeScore),
    details: {
      matchedSkills,
      missingSkills,
      experienceMatch,
      salaryMatch,
      locationMatch,
      jobTypeMatch,
    },
  };
}

/**
 * Get a color class for the match score
 */
export function getMatchScoreColor(score: number): string {
  if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
  if (score >= 60) return 'text-orange-600 bg-orange-50 border-orange-200';
  return 'text-red-600 bg-red-50 border-red-200';
}

/**
 * Get a label for the match score
 */
export function getMatchScoreLabel(score: number): string {
  if (score >= 90) return 'Excellent Match';
  if (score >= 80) return 'Great Match';
  if (score >= 70) return 'Good Match';
  if (score >= 60) return 'Fair Match';
  return 'Low Match';
}
